// Quote Builder — retailer profile fetcher.
//
// Fetches the calling retailer's branding + Twilio number for use on the
// customer-facing PDF header/footer. The `phone` field on the retailers
// table is the retailer's PERSONAL mobile and is intentionally NOT
// exposed by the server endpoint — the platform Twilio number is the
// only contact number the customer ever sees.

import { api } from "@tkos/api-client";

export interface RetailerProfile {
  id: string;
  businessName: string;
  slug: string;
  email: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    county: string;
    postcode: string;
    country: string;
  };
  website: string;
  logoUrl: string;
  primaryColour: string;
  secondaryColour: string;
  twilioNumber: string;
  /** Whether the retailer is VAT registered — seeds a new quote's default VAT
   *  rate (registered → 20, otherwise ex-VAT). */
  vatRegistered: boolean;
  /** Legally-required VAT identifier shown on customer-facing VAT documents. */
  vatNumber: string;
  /** Retailer's default intro/closing quote notes (Settings → Company Profile),
   *  used to seed a new quote. */
  quoteIntroDefault?: string;
  quoteClosingDefault?: string;
}

let cached: RetailerProfile | null = null;
let cacheAt = 0;
const TTL_MS = 60_000;

export async function fetchRetailerProfile(force = false): Promise<RetailerProfile | null> {
  if (!force && cached && Date.now() - cacheAt < TTL_MS) return cached;
  try {
    const res = await api<{ ok: boolean; profile: RetailerProfile }>(`/portal/retailer-profile`);
    if (!res.profile) return null;
    cached = res.profile;
    cacheAt = Date.now();
    return cached;
  } catch (err) {
    console.warn("[qb-retailer-profile] fetch failed", err);
    return null;
  }
}

/** Build a single-line address string: "line1, line2, city, county postcode". */
export function formatAddressLine(p: RetailerProfile | null): string {
  if (!p) return "";
  const a = p.address;
  const parts = [a.line1, a.line2, a.city, a.county, a.postcode].filter((s) => s && s.trim().length > 0);
  return parts.join(", ");
}

/** Multi-line address (newline-separated) for footers with vertical space. */
export function formatAddressBlock(p: RetailerProfile | null): string[] {
  if (!p) return [];
  const a = p.address;
  const lines: string[] = [];
  if (a.line1) lines.push(a.line1);
  if (a.line2) lines.push(a.line2);
  const cityLine = [a.city, a.county, a.postcode].filter(Boolean).join(", ");
  if (cityLine) lines.push(cityLine);
  return lines;
}
